import type { MachineDetail } from '@/api/resources/machines'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { machineOperatorsQueryOptions } from '@/api/resources/machine-operators'
import { machineQueryOptions, updateMachine } from '@/api/resources/machines'
import { deleteRevenuePricingRule, upsertRevenuePricingRule } from '@/api/resources/pricing'
import { LoadingSpinner } from '@/components/feedback/loading-spinner'
import { MachineDetailHeader } from '@/components/machines/machine-detail-header'
import { MachineOperatorsPanel } from '@/components/machines/operators/machine-operators-panel'
import { MachinePlanogramPanel } from '@/components/machines/planogram/machine-planogram-panel'
import { SalesOverviewPanel } from '@/components/machines/sales-overview-panel'
import { queryKeys } from '@/lib/query-keys'

export const Route = createFileRoute('/_app/machines/$machineCode')({
  head: ({ params }) => ({ meta: [{ title: `Machine ${params.machineCode} - MyJunea` }] }),
  loader: ({ context, params }) => {
    void context.queryClient.ensureQueryData(machineQueryOptions(params.machineCode))
    void context.queryClient.ensureQueryData(machineOperatorsQueryOptions(params.machineCode))
  },
  component: MachineDetailLayout,
})

function MachineDetailLayout(): React.ReactElement {
  const { machineCode } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: machine, isLoading: isMachineLoading } = useQuery(machineQueryOptions(machineCode))
  const { data: operators, isLoading: isOperatorsLoading } = useQuery(
    machineOperatorsQueryOptions(machineCode),
  )

  const priceMutation = useMutation({
    mutationFn: async ({ productId, price }: { productId: number; price: number }) =>
      upsertRevenuePricingRule({ machine_code: machineCode, product_id: productId, price }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.machines.detail(machineCode) })
      toast.success('Price updated')
    },
    onError: () => {
      toast.error('Failed to update price')
    },
  })

  const priceResetMutation = useMutation({
    mutationFn: async (productId: number) =>
      deleteRevenuePricingRule({ machine_code: machineCode, product_id: productId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.machines.detail(machineCode) })
      toast.success('Price reset to default')
    },
    onError: () => {
      toast.error('Failed to reset price')
    },
  })

  const nameMutation = useMutation({
    mutationFn: async (name: string) =>
      updateMachine(machineCode, { name, location_id: machine?.location_id ?? null }),
    onMutate: async (name) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.machines.detail(machineCode) })
      const previous = queryClient.getQueryData<MachineDetail>(
        queryKeys.machines.detail(machineCode),
      )
      queryClient.setQueryData<MachineDetail>(queryKeys.machines.detail(machineCode), (old) =>
        old !== undefined ? { ...old, name } : old,
      )
      return { previous }
    },
    onError: (_error, _name, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKeys.machines.detail(machineCode), context.previous)
      }
      toast.error('Failed to update name')
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.machines.detail(machineCode) })
    },
  })

  if (isMachineLoading) {
    return <LoadingSpinner />
  }

  if (machine === undefined) {
    return <p className="text-muted-foreground py-10 text-center">Machine not found.</p>
  }

  const isRevenuePricing = machine.subscription_type === 'revenue'

  const handleSlotClick = (slotId: number): void => {
    void navigate({
      to: '/machines/$machineCode/slots/$slotId/replace',
      params: { machineCode, slotId: String(slotId) },
      resetScroll: false,
    })
  }

  const handleAddOperatorClick = (): void => {
    void navigate({
      to: '/machines/$machineCode/operators/add',
      params: { machineCode },
      resetScroll: false,
    })
  }

  const handleOperatorClick = (operatorId: number): void => {
    void navigate({
      to: '/machines/$machineCode/operators/$userId/edit',
      params: { machineCode, userId: String(operatorId) },
      resetScroll: false,
    })
  }

  return (
    <div className="space-y-6">
      <MachineDetailHeader
        name={machine.name}
        code={machine.code}
        type={machine.type}
        typeName={machine.type_name}
        status={machine.status}
        statusCategory={machine.status_category}
        stock={machine.stock}
        capacity={machine.capacity}
        emptySlots={machine.empty_slots}
        locationName={machine.location?.name ?? null}
        subscriptionName={machine.subscription_name}
        onNameSave={(name) => nameMutation.mutate(name)}
      />
      <div className="flex flex-col gap-5 md:flex-row">
        {machine.sales_overview !== null && (
          <SalesOverviewPanel
            type={machine.sales_overview.type}
            total={machine.sales_overview.total}
            months={machine.sales_overview.months}
            className="min-w-0 flex-[2]"
          />
        )}
        <MachineOperatorsPanel
          className="flex-1 md:self-start"
          operators={(operators ?? []).map((operator) => ({
            id: operator.id,
            firstName: operator.first_name,
            lastName: operator.last_name,
            email: operator.email,
            emailOptin: operator.email_optin,
          }))}
          isLoading={isOperatorsLoading}
          onAddClick={handleAddOperatorClick}
          onOperatorClick={handleOperatorClick}
        />
      </div>
      <MachinePlanogramPanel
        shelves={machine.shelves}
        onSlotClick={handleSlotClick}
        priceEdit={
          isRevenuePricing
            ? {
                onSave: (productId, price) => priceMutation.mutate({ productId, price }),
                onReset: (productId) => priceResetMutation.mutate(productId),
                isPending: (productId: number) =>
                  (priceMutation.isPending && priceMutation.variables?.productId === productId) ||
                  (priceResetMutation.isPending && priceResetMutation.variables === productId),
              }
            : undefined
        }
      />
      <Outlet />
    </div>
  )
}
